<template>
  <div
    :class="{ 'tw-flex-col': verticalMenuTab }"
    class="tw-flex tw-flex-[1_0_0] tw-items-center tw-gap-2 max-[350px]:tw-gap-1 md:tw-gap-4"
  >
    <!-- Shortlist -->
    <button
      type="button"
      class="tw-relative tw-flex tw-h-[44px] tw-w-[44px] tw-items-center tw-justify-center tw-rounded-[25px] tw-border focus:tw-outline-none focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:focus-visible:tw-ring-0 md:tw-h-[50px] md:tw-w-[50px]"
      data-testid="search-nav-shortlist"
      :class="{
        'tw-border-[#d1d5db] hover:tw-border-uhblue-700': !shortlistActive,
        'tw-border-transparent tw-bg-[#0082b8] tw-text-white hover:tw-border-transparent':
          shortlistActive,
      }"
      aria-label="View your shortlisted properties"
      @click="$emit('toggle-short-list')"
    >
      <Shortlists
        :stroke-color="shortlistActive"
        :has-shortlisted-properties="hasShortlistedProperties"
      />
    </button>

    <!-- Housemate Group -->
    <button
      v-if="enableHousemateGroups && showHousemateGroupTab"
      type="button"
      data-testid="search-nav-housemate-groups"
      class="tw-relative tw-flex tw-h-[44px] tw-w-[44px] tw-items-center tw-justify-center tw-rounded-[25px] tw-border focus:tw-outline-none focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:focus-visible:tw-ring-0 md:tw-h-[50px] md:tw-w-[50px]"
      :class="{
        'tw-border-[#d1d5db] hover:tw-border-uhblue-700': !housemateGroupActive,
        'tw-border-transparent tw-bg-[#0082b8] tw-text-white hover:tw-border-transparent':
          housemateGroupActive,
      }"
      aria-label="Create a new housemate group"
      @click="$emit('toggle-housemate-group')"
    >
      <HousemateGroup :stroke-color="housemateGroupActive" />
    </button>

    <!-- Enquiries -->
    <button
      type="button"
      data-testid="search-nav-enquiries"
      class="tw-flex tw-h-[44px] tw-w-[44px] tw-items-center tw-justify-center tw-rounded-[25px] tw-border focus:tw-outline-none focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:focus-visible:tw-ring-0 md:tw-h-[50px] md:tw-w-[50px]"
      :class="{
        'tw-border-[#d1d5db] hover:tw-border-uhblue-700': !userEnquiriesActive,
        'tw-border-transparent tw-bg-[#0082b8] tw-text-white hover:tw-border-transparent':
          userEnquiriesActive,
      }"
      aria-label="View your account enquiries"
      @click="$emit('toggle-user-enquiry')"
    >
      <Enquiries :stroke-color="userEnquiriesActive" />
    </button>
  </div>
</template>

<script>
import { useStudentStore } from '@js/stores/student/student';
import Enquiries from '@components/Student/SVG/Enquiries.vue';
import Shortlists from '@components/Student/SVG/Shortlists.vue';
import HousemateGroup from '@components/Student/SVG/HousemateGroup.vue';

export default {
  name: 'SearchNavSubMenu',

  components: {
    HousemateGroup,
    Shortlists,
    Enquiries,
  },
  props: {
    shortlistActive: {
      type: Boolean,
      default: false,
    },
    isLoggedInStudent: {
      type: Boolean,
      default: false,
    },
    housemateGroupActive: {
      type: Boolean,
      default: false,
    },
    userEnquiriesActive: {
      type: Boolean,
      default: false,
    },
    hasEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    showHousemateGroupTab: {
      type: Boolean,
      default: true,
    },
    enableHousemateGroups: {
      type: Boolean,
      default: false,
    },
    verticalMenuTab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      studentStore: this.isLoggedInStudent
        ? useStudentStore()
        : { shortlistedPropertiesCount: 0 },
      housemateGroupToolTipActive: false,
      enquiryToolTipActive: false,
      activeTooltip: null,
      viewportWidth: document.documentElement.clientWidth,
      resizeTimer: null,
      tooltipTimeout: null,
    };
  },
  computed: {
    hasShortlistedProperties() {
      return this.studentStore.shortlistedPropertiesCount > 0;
    },
    enquiriesDisabled() {
      return !this.isLoggedInStudent;
    },
    tooltipMobileWidth() {
      const horizontalPadding = 35; // Allows to align content more accurately to the property card with on mobile
      const minWidth = 260;

      const width = Math.max(minWidth, this.viewportWidth - horizontalPadding);

      // Style object approach used here to prevent rendering issues
      return {
        width: `${width}px`,
        maxWidth: `${width}px`,
      };
    },
  },
  mounted() {
    this.handleResize = () => {
      clearTimeout(this.resizeTimer);

      this.resizeTimer = setTimeout(() => {
        this.viewportWidth = document.documentElement.clientWidth;
      }, 80);
    };

    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimer);
  },
  methods: {
    handleCloseToolTip() {
      this.activeTooltip = null;
    },
    handleTooltipEnter(name) {
      this.activeTooltip = name;

      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
      }

      this.tooltipTimeout = setTimeout(() => {
        this.activeTooltip = null;
        this.tooltipTimeout = null;
      }, 3000);
    },
    handleTooltipLeave(e) {
      //Handles mouse enter and leave edge cases
      if (e?.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
        return;
      }
      this.activeTooltip = null;
    },
  },
};
</script>
<style scoped>
.disabled-btn {
  -webkit-tap-highlight-color: transparent;
}
</style>
